import { useEffect, useRef } from 'react';
import { useRecordProductViewMutation } from '../api/productExperienceApi';

import { attentionEpisode } from './productAttention';
type Entry = { element: HTMLElement; detail: boolean; record: () => void; state: Parameters<typeof attentionEpisode>[0] };
const entries = new Set<Entry>();
let interval: number | undefined;
let touch: { x: number; y: number; at: number } | undefined;
let lastScroll = 0;
let lastFocus = 0;
let memoryVisitor = '';
function visitorId() {
  if (memoryVisitor) return memoryVisitor;
  memoryVisitor = crypto.randomUUID();
  try {
    const stored = localStorage.getItem('ys:product-visitor');
    if (stored && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(stored)) memoryVisitor = stored;
    else localStorage.setItem('ys:product-visitor', memoryVisitor);
  } catch { /* Session-only identity when storage is blocked. */ }
  return memoryVisitor;
}
function tick() {
  const now = performance.now();
  let winner: Entry | undefined;
  let best = -Infinity;
  if (document.visibilityState === 'visible' && document.hasFocus() && now - lastScroll > 250) {
    for (const entry of entries) {
      const r = entry.element.getBoundingClientRect();
      const left = Math.max(0, r.left), right = Math.min(innerWidth, r.right);
      const top = Math.max(0, r.top), bottom = Math.min(innerHeight, r.bottom);
      const area = Math.min(innerWidth, r.width) * Math.min(innerHeight, r.height);
      const visible = Math.max(0, right - left) * Math.max(0, bottom - top);
      if (!area || visible / area < .6) continue;
      const x = (left + right) / 2, y = (top + bottom) / 2;
      if (!entry.element.contains(document.elementFromPoint(x, y))) continue;
      const focused = entry.element.contains(document.activeElement);
      const touched = touch && now - touch.at < 6000 && entry.element.contains(document.elementFromPoint(touch.x, touch.y));
      const score = (focused && lastFocus >= (touch?.at ?? 0) ? 10000 : touched ? 5000 : focused ? 3000 : entry.detail ? 2000 : 0) - Math.hypot(x - innerWidth / 2, y - innerHeight / 2);
      if (score > best) { best = score; winner = entry; }
    }
  }
  for (const entry of entries) if (attentionEpisode(entry.state, entry === winner, now)) entry.record();
}
const onTouch = (event: PointerEvent) => { touch = { x: event.clientX, y: event.clientY, at: performance.now() }; };
const onFocus = () => { lastFocus = performance.now(); };
const onScroll = () => { lastScroll = performance.now(); };
function start() {
  if (interval !== undefined) return;
  document.addEventListener('focusin', onFocus);
  document.addEventListener('pointerdown', onTouch, { passive: true });
  document.addEventListener('scroll', onScroll, { passive: true, capture: true });
  interval = window.setInterval(tick, 250);
}
function stop() {
  if (entries.size || interval === undefined) return;
  clearInterval(interval); interval = undefined; touch = undefined;
  document.removeEventListener('focusin', onFocus);
  document.removeEventListener('pointerdown', onTouch);
  document.removeEventListener('scroll', onScroll, true);
}
/** Only one visible product is counted at a time; repeat episodes need a fresh dwell. */
export function useProductAttention(slug: string | undefined, detail = false) {
  const ref = useRef<HTMLDivElement>(null);
  const [record] = useRecordProductViewMutation();
  useEffect(() => {
    if (!slug || !ref.current) return;
    const entry: Entry = { element: ref.current, detail, state: { since: null, counted: false, leftAt: null },
      record: () => { void record({ slug, visitorId: visitorId(), eventId: crypto.randomUUID() }); } };
    entries.add(entry); start();
    return () => { entries.delete(entry); stop(); };
  }, [slug, detail, record]);
  return ref;
}
